import { HTMLAttributes } from "react";
import "./AnimatedIcons.scss";

// Composer micro-icons with hover animations, ported from lucide-animated.com
// (MIT). Geometry is the upstream Lucide SVG verbatim; the animations are
// redone in plain CSS (see AnimatedIcons.scss) because the upstream components
// need the `motion` runtime — too heavy a dependency for three icons in the
// widget bundle. Icons inherit their color via `currentColor` (stroke-based,
// unlike the fill-based FontAwesome assets they replaced).

interface AnimatedIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
}

const iconClassName = (variant: string, className?: string): string =>
  ["animated-icon", `animated-icon--${variant}`, className]
    .filter(Boolean)
    .join(" ");

export const AttachFileIcon = ({
  size = 19,
  className,
  ...props
}: AnimatedIconProps) => (
  <div className={iconClassName("attach", className)} {...props}>
    <svg
      fill="none"
      height={size}
      width={size}
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* pathLength=1 normalizes the dash units so the CSS draw-on animation
          can sweep stroke-dashoffset 1 → 0 regardless of real path length. */}
      <path
        className="animated-icon-attach-path"
        pathLength={1}
        d="M6 7.90909V16C6 19.3137 8.68629 22 12 22V22C15.3137 22 18 19.3137 18 16V6C18 3.79086 16.2091 2 14 2V2C11.7909 2 10 3.79086 10 6V15.1818C10 16.2864 10.8954 17.1818 12 17.1818V17.1818C13.1046 17.1818 14 16.2864 14 15.1818V8"
      />
    </svg>
  </div>
);

export const MicIcon = ({
  size = 19,
  className,
  ...props
}: AnimatedIconProps) => (
  <div className={iconClassName("mic", className)} {...props}>
    {/* overflow visible: the capsule bobs above the viewBox top edge. */}
    <svg
      fill="none"
      height={size}
      width={size}
      overflow="visible"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M12 19v3" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <rect
        className="animated-icon-mic-capsule"
        height="13"
        rx="3"
        width="6"
        x="9"
        y="2"
      />
    </svg>
  </div>
);

export const DeleteIcon = ({
  size = 19,
  className,
  ...props
}: AnimatedIconProps) => (
  <div className={iconClassName("delete", className)} {...props}>
    <svg
      fill="none"
      height={size}
      width={size}
      overflow="visible"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g className="animated-icon-delete-lid">
        <path d="M3 6h18" />
        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
      </g>
      <path d="M19 8v12c0 1-1 2-2 2H7c-1 0-2-1-2-2V8" />
      <line className="animated-icon-delete-line" x1="10" x2="10" y1="11" y2="17" />
      <line className="animated-icon-delete-line" x1="14" x2="14" y1="11" y2="17" />
    </svg>
  </div>
);
